import { useCallback, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { libraryQueryKey } from "@/lib/data/react-query/file-library";
import {
  listLibrary,
  type LibraryItem,
  type LibraryScope,
} from "@/lib/data/requests/file-library";

type FolderPage = { parentId: string; cursor?: string };

export function useLibraryTree(scope: LibraryScope, enabled: boolean) {
  const [pages, setPages] = useState<FolderPage[]>([{ parentId: "" }]);
  const queries = useQueries({
    queries: pages.map(({ parentId, cursor }) => ({
      queryKey: [...libraryQueryKey, "tree", scope, parentId, cursor],
      queryFn: () => listLibrary(scope, parentId || undefined, cursor),
      enabled,
    })),
  });
  const itemsById = new Map<string, LibraryItem>();
  queries.forEach((query) => {
    query.data?.data.forEach((item) => itemsById.set(item.id, item));
  });
  const items = Array.from(itemsById.values());
  const unloadedFolderIds = new Set(
    items
      .filter(
        (item) =>
          item.isFolder &&
          item.hasChildren &&
          !pages.some((page) => page.parentId === item.id),
      )
      .map((item) => item.id),
  );
  const loadFolder = useCallback((parentId: string) => {
    setPages((current) =>
      current.some((page) => page.parentId === parentId)
        ? current
        : [...current, { parentId }],
    );
  }, []);
  const folderState = (parentId: string) => {
    const indexes = pages.flatMap((page, index) =>
      page.parentId === parentId ? [index] : [],
    );
    const folderQueries = indexes.map((index) => queries[index]);
    const last = folderQueries.at(-1);
    const nextCursor = last?.data?.nextCursor;
    return {
      loading: folderQueries.some((query) => query.isPending),
      error: folderQueries.find((query) => query.error)?.error,
      hasMore: !!nextCursor,
      retry: () => {
        folderQueries.forEach((query) => void query.refetch());
      },
      loadMore: () => {
        if (!nextCursor) return;
        setPages((current) =>
          current.some(
            (page) => page.parentId === parentId && page.cursor === nextCursor,
          )
            ? current
            : [...current, { parentId, cursor: nextCursor }],
        );
      },
    };
  };
  return { items, itemsById, unloadedFolderIds, loadFolder, folderState };
}
